/**
 * P&L Overview — cumulative P&L curves per agent using lightweight-charts.
 */

import { useEffect, useRef } from "react";
import { createChart, type IChartApi } from "lightweight-charts";
import { useAgentLeaderboard } from "@/sdk/hooks/use-agents";
import { useTradingBalance, interpretWalletBalance } from "@/sdk/hooks/use-trading";
import { asCurrency } from "@/lib/format";

const AGENT_COLORS = [
  "#6366f1", // indigo
  "#22d3ee", // cyan
  "#f59e0b", // amber
  "#10b981", // emerald
  "#f43f5e", // rose
  "#a855f7", // purple
  "#3b82f6", // blue
  "#ef4444", // red
];

export function PnlOverviewPanel() {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartApiRef = useRef<IChartApi | null>(null);
  const {
    data: leaderboard,
    isError: leaderboardError,
    isPending: leaderboardLoading,
  } = useAgentLeaderboard();

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = createChart(chartRef.current, {
      layout: {
        background: { color: "#09090b" },
        textColor: "#a1a1aa",
        fontSize: 11,
      },
      grid: {
        vertLines: { color: "#27272a" },
        horzLines: { color: "#27272a" },
      },
      crosshair: {
        vertLine: { color: "#6366f1", labelBackgroundColor: "#6366f1" },
        horzLine: { color: "#6366f1", labelBackgroundColor: "#6366f1" },
      },
      rightPriceScale: {
        borderColor: "#27272a",
      },
      timeScale: {
        borderColor: "#27272a",
        timeVisible: true,
      },
    });

    chartApiRef.current = chart;

    const handleResize = () => {
      if (chartRef.current) {
        chart.applyOptions({
          width: chartRef.current.clientWidth,
          height: chartRef.current.clientHeight,
        });
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(chartRef.current);
    handleResize();

    return () => {
      resizeObserver.disconnect();
      chart.remove();
      chartApiRef.current = null;
    };
  }, []);

  const {
    data: balanceData,
    isError: balanceError,
    isPending: balanceLoading,
  } = useTradingBalance(30_000);
  // Three states: a real balance, a still-loading read ("…"), and a failed read
  // ("Unavailable"). A failed read must never show a misleading "$0.00", and a
  // load-in-progress must never flash "Unavailable".
  const wallet = interpretWalletBalance(balanceData, {
    isError: balanceError,
    isLoading: balanceLoading,
  });

  // Display leaderboard stats below chart
  const agents = leaderboard?.leaderboard ?? [];
  const totalPnl = agents.reduce((s, a) => s + (a.total_pnl ?? 0), 0);

  return (
    <div className="flex h-full flex-col bg-zinc-950">
      {/* Chart area */}
      <div ref={chartRef} className="min-h-0 flex-1" />

      {/* Agent P&L summary bar */}
      <div className="flex items-center gap-4 border-t border-zinc-800 px-3 py-1.5 text-xs">
        {/* Bug #19 (audit 2026-05-06): route through asCurrency so a NaN or
            +/-Infinity from a misbehaving balance API surfaces as "n/a" rather
            than "$NaN" / "$∞". 2026-06-01: a FAILED on-chain read (geoblock /
            creds missing) returns usdc:0 — show "Unavailable", never "$0.00". */}
        <span className="font-bold text-white">
          {wallet.status === "available" ? wallet.label ?? "Wallet" : "Wallet"}:{" "}
          {wallet.status === "available"
            ? asCurrency(wallet.usdc)
            : wallet.status === "loading"
              ? "…"
              : "Unavailable"}
        </span>
        {/* Same three-state honesty as the wallet half of this bar: a failed
            leaderboard read reduces an EMPTY array to 0 — that is not a real
            $0.00 P&L, so render "Unavailable" instead. */}
        {leaderboardError ? (
          <span className="font-bold text-amber-400">P&L: Unavailable</span>
        ) : leaderboardLoading ? (
          <span className="font-bold text-zinc-400">P&L: …</span>
        ) : (
          <span className={`font-bold ${totalPnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
            P&L: {asCurrency(totalPnl)}
          </span>
        )}
        {agents.map((a, i) => (
          <span key={a.agent_id} className="flex items-center gap-1.5 text-zinc-400">
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: AGENT_COLORS[i % AGENT_COLORS.length] }}
            />
            {a.name}:
            <span className={(a.total_pnl ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"}>
              ${(a.total_pnl ?? 0).toFixed(0)}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}
